// Repairs GFM table delimiter rows whose cell count does not match the header.
//
// The synthesized answer occasionally arrives with a header of N columns over
// a delimiter row of N-1 (or N+1) cells, e.g.
//
//   | Priority | Account | ARR |
//   |---|---|
//
// GFM requires the delimiter row to match the header exactly, so the whole
// table falls back to a paragraph of raw pipes. This pads (with `---`) or
// truncates the delimiter row to the header width, preserving the alignment
// markers of the cells it keeps. Everything else is passed through untouched:
// fenced code blocks, rows that are not a header+delimiter pair, and a
// delimiter row that is still streaming in (no trailing newline yet), since
// the next token may complete it.
//
// Cell counting follows GFM: an escaped `\|` and a pipe inside an inline code
// span are cell content, not boundaries, so a valid header such as
// `| Segment \| Tier | ARR |` is two cells and is left alone.
//
// Runs as Streamdown's `preprocess` on every token update, so it is a single
// linear pass over lines with no regex backtracking.

import { closesFence, openingFence, type FenceMarker } from './markdownCodeGuards';

const DELIMITER_CELL = /^\s*:?-+:?\s*$/;

// Splits a table row into cells on unescaped pipes outside inline code.
// Returns null when the line has no pipe at all.
export function splitTableRow(line: string): string[] | null {
  const trimmed = line.trim();
  if (!trimmed.includes('|')) return null;
  const cells: string[] = [];
  let current = '';
  let inCode = false;
  for (let index = 0; index < trimmed.length; index += 1) {
    const char = trimmed[index];
    if (char === '\\' && index + 1 < trimmed.length) {
      current += char + trimmed[index + 1];
      index += 1;
      continue;
    }
    if (char === '`') inCode = !inCode;
    if (char === '|' && !inCode) {
      cells.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  cells.push(current);
  // A leading pipe yields an empty first cell; a trailing pipe an empty last one.
  if (cells.length > 1 && cells[0].trim() === '' && trimmed.startsWith('|')) cells.shift();
  if (cells.length > 1 && cells[cells.length - 1].trim() === '' && trimmed.endsWith('|') && !trimmed.endsWith('\\|')) cells.pop();
  return cells;
}

function isDelimiterRow(cells: string[]): boolean {
  return cells.length > 0 && cells.every((cell) => DELIMITER_CELL.test(cell));
}

export function normalizeMarkdownTables(markdown: string): string {
  if (!markdown.includes('|')) return markdown;
  const lines = markdown.split('\n');
  const endsWithNewline = markdown.endsWith('\n');
  let fence: FenceMarker | null = null;
  let changed = false;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    if (fence) {
      if (closesFence(line, fence)) fence = null;
      continue;
    }
    const opened = openingFence(line);
    if (opened) {
      fence = opened;
      continue;
    }
    if (index === 0) continue;
    // The last line of a still-streaming chunk may be a partial delimiter row.
    if (index === lines.length - 1 && !endsWithNewline) break;

    const delimiterCells = splitTableRow(line);
    if (!delimiterCells || !isDelimiterRow(delimiterCells)) continue;
    const headerCells = splitTableRow(lines[index - 1]);
    if (!headerCells || isDelimiterRow(headerCells)) continue;
    if (headerCells.length === delimiterCells.length) continue;

    const width = headerCells.length;
    const kept = delimiterCells.slice(0, width).map((cell) => cell.trim());
    while (kept.length < width) kept.push('---');
    lines[index] = `|${kept.join('|')}|`;
    changed = true;
  }

  return changed ? lines.join('\n') : markdown;
}
